import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { map, tap } from 'rxjs/operators';
import { EntiteModel } from '../../../models/entites/entite.model';
import { EntitesService } from '../../../services/entites/entites.service';

class EntiteModelExtended extends EntiteModel {
  constructor(id: number, code: string, libelle: string, actif: boolean, public editable: boolean = true) {
    super(id, code, libelle, actif);
  }
}

@Component({
  selector: 'app-entite-selector-multi',
  templateUrl: './entite-selector-multi.component.html',
  styleUrls: ['./entite-selector-multi.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EntiteSelectorMultiComponent),
      multi: true,
    },
  ],
})
export class EntiteSelectorMultiComponent implements OnInit {
  selected: number[] = [];
  formControl: UntypedFormControl = new UntypedFormControl(null, this.getRemoveExistingEntityForbiddenValidator());
  private entitesUtilisateur: EntiteModelExtended[] = [];
  public allEntites: EntiteModelExtended[] = [];
  entitesVisibles: EntiteModelExtended[] = [];
  readonly proposedValuesFilter = (item: EntiteModelExtended) =>
    this.isEntiteSelectable(item);

  private visible = true;
  @Input() filter: number[] = null;
  @Input() hideIfSingleEntite = true;

  private pendingSelectedIds: number[] | null = null;
  public entitesGerable: EntiteModelExtended[] = [];
  private destroyRef = inject(DestroyRef);
  
  constructor(private entitesService: EntitesService) {
    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(newValue => {
      const hasChanged = !this.sameLists(newValue, this.selected);
      this.selected = newValue;
      if (hasChanged && this.onChange != null && this.formControl.dirty) {
        this.onChange(newValue);
      }
    });

    this.loadEntites();
  }

  ngOnInit(): void {}

  private getRemoveExistingEntityForbiddenValidator() {
    return (formControl: UntypedFormControl) => {
      if (!formControl.dirty) {
        return null;
      }
      const previousEntites = (this.selected ?? []).map(id => this.getEntiteById(id)).filter(Boolean);
      const newEntites = (formControl.value ?? []).map(id => this.getEntiteById(id)).filter(Boolean);

      if (previousEntites.length > 0 && newEntites.length > 0 &&
          previousEntites.some((e: EntiteModelExtended) => e && e.editable === false && !newEntites.find((e2: EntiteModelExtended) => e2.id == e.id))) {
        return {notAllowed: true};
      }
      return null;
    };
  }

  private convertEntiteModelToExtended(editable: boolean = true) {
    return (entite: EntiteModel): EntiteModelExtended => {
      const isGerable = (entite as any).isGerableUtilisateur;
      const isEditable = editable && this.isEntiteActive(entite) && isGerable;
      const extended = new EntiteModelExtended(entite.id, entite.code, entite.libelle, entite.active, isEditable);
      (extended as any).isGerableUtilisateur = isGerable;
      return extended;
    };
  }

  private defineEntitesVisibles() {
    this.entitesVisibles = this.applyFilter([...(this.entitesGerable ?? [])]);
    if (this.selected?.length > 0) {
      this.addEntitesToChoiceListIfExist(this.selected);
    }
  }

  private isEntiteActive(entite: EntiteModel): boolean {
    return entite.active ?? entite.actif;
  }

  private isEntiteSelectable(entite: EntiteModelExtended): boolean {
    return this.isEntiteActive(entite) && entite.isGerableUtilisateur === true;
  }

  private loadEntites() {
    this.entitesService.getUtilisateursEntitesGerables()
      .pipe(
        map(entites => {
          this.allEntites = entites.map(this.convertEntiteModelToExtended());
          this.entitesGerable = entites
            .filter(e => this.isEntiteActive(e) && e.isGerableUtilisateur)
            .map(this.convertEntiteModelToExtended());
          this.entitesUtilisateur = this.entitesGerable;
          return entites;
        }),
        tap((entites: EntiteModel[]) => {
          const activesEtGerables = entites.filter(e => this.isEntiteActive(e) && e.isGerableUtilisateur);
          if (this.hideIfSingleEntite) {
            this.visible = activesEtGerables.length > 1;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.defineEntitesVisibles();
        if (this.pendingSelectedIds && this.pendingSelectedIds.length > 0) {
          this.addEntitesToChoiceListIfExist(this.pendingSelectedIds);
          this.selected = this.pendingSelectedIds;
          this.formControl.reset(this.pendingSelectedIds);
          this.pendingSelectedIds = null;
        }
      });
  }

  private getEntiteById(id: number): EntiteModelExtended | null {
    if (!id) { return null; }
    const fromAll = (this.allEntites || []).find(it => it.id == id);
    return fromAll ?? null;
  }

  get isHidden() {
    return this.visible == false;
  }

  onChange: (value) => any = v => {};

  registerOnChange(fn: (value) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    // noop si pas utilisé
  }

  private entiteExists(id: number) {
    return (this.allEntites || []).findIndex(it => it.id == id) >= 0;
  }

  private isEntiteActuellementProposeeInSelector(id: number) {
    return (this.entitesVisibles || []).findIndex(it => it.id == id) >= 0;
  }

  sameLists(list1: number[], list2: number[]) {
    return list1 != null && list2 != null && list1.length == list2.length && list1.every(id => list2.indexOf(id) >= 0);
  }

  writeValue(ids: number[]): void {
    if (this.sameLists(ids, this.selected)) {
      return;
    }

    if (!this.allEntites || this.allEntites.length === 0) {
      this.pendingSelectedIds = ids;
      this.selected = ids ?? [];
      return;
    }

    this.addEntitesToChoiceListIfExist(ids);
    this.selected = ids ?? [];
    if (this.onChange) { this.onChange(ids); }
    this.formControl.reset(ids);
    this.defineEntitesVisibles();
  }

  private addEntitesToChoiceListIfExist(ids: number[]) {
    if (!ids?.length) {
      return;
    }

    const entitesAjoutees = ids
      .filter(id => !this.isEntiteActuellementProposeeInSelector(id) && this.entiteExists(id))
      .map(id => this.getEntiteById(id))
      .filter((entite): entite is EntiteModelExtended => entite != null);

    if (entitesAjoutees.length > 0) {
      this.entitesVisibles = [...this.entitesVisibles, ...entitesAjoutees];
    }
  }

  private applyFilter(entites: EntiteModelExtended[]) {
    if (this.filter != null) {
      return entites.filter(e => this.filter.indexOf(e.id) >= 0);
    } else {
      return [...entites];
    }
  }

}
